import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { Calendar } from "../components/ui/calendar";
import { Check, X, LogOut, Ban, MapPinned } from "lucide-react";

const STATUS_BADGE = {
  pending: "bg-[#EDE3DA] text-[#5E7891]",
  approved: "bg-[#EBE0C9] text-[#8A6A30]",
  rejected: "bg-[#EBD0D4] text-[#9B5E68]",
};

function toLocalDateString(d) {
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [pickerDate, setPickerDate] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [b, bd] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/blocked-dates"),
      ]);
      setBookings(b.data || []);
      setBlocked(bd.data || []);
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { if (user) load(); }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const decide = async (id, status) => {
    setBusy(true);
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(status === "approved" ? "Booking approved" : "Booking rejected");
      await load();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const blockDate = async () => {
    if (!pickerDate) { toast.error("Pick a date first"); return; }
    const d = toLocalDateString(pickerDate);
    try {
      await api.post("/admin/blocked-dates", { date: d });
      toast.success(`Blocked ${d}`);
      setPickerDate(null);
      await load();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || e.message);
    }
  };

  const unblock = async (d) => {
    try {
      await api.delete(`/admin/blocked-dates/${d}`);
      toast.success(`Unblocked ${d}`);
      await load();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || e.message);
    }
  };

  const pending = bookings.filter((b) => b.status === "pending").length;
  const approved = bookings.filter((b) => b.status === "approved").length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F7F1E8]">
      <header className="bg-[#FDFAF3] border-b border-[#E5D9C0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-serif-display text-xl">Porto</span>
            <span className="font-script text-[#5E7891]">admin</span>
          </div>
          <button data-testid="admin-logout" onClick={logout}
            className="inline-flex items-center gap-2 text-sm text-[#5E7891] hover:text-[#2D3640]">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="p-6 rounded-2xl bg-[#FDFAF3] border border-[#E5D9C0]">
            <p className="text-xs uppercase tracking-[0.22em] text-[#5E7891]">Pending</p>
            <p className="font-serif-display text-4xl mt-2" data-testid="stat-pending">{pending}</p>
          </div>
          <div className="p-6 rounded-2xl bg-[#FDFAF3] border border-[#E5D9C0]">
            <p className="text-xs uppercase tracking-[0.22em] text-[#5E7891]">Approved</p>
            <p className="font-serif-display text-4xl mt-2" data-testid="stat-approved">{approved}</p>
          </div>
          <div className="p-6 rounded-2xl bg-[#FDFAF3] border border-[#E5D9C0]">
            <p className="text-xs uppercase tracking-[0.22em] text-[#5E7891]">Total</p>
            <p className="font-serif-display text-4xl mt-2" data-testid="stat-total">{bookings.length}</p>
          </div>
        </div>

        <div className="mt-10 grid lg:grid-cols-12 gap-8">
          <section className="lg:col-span-8">
            <h2 className="font-serif-display text-2xl md:text-3xl mb-5">Booking requests</h2>
            <div data-testid="bookings-list" className="space-y-3">
              {bookings.length === 0 && (
                <p className="text-sm text-[#5E7891] italic">No bookings yet.</p>
              )}
              {bookings.map((b) => (
                <div key={b.id} data-testid={`booking-row-${b.id}`}
                  className="p-5 rounded-2xl bg-[#FDFAF3] border border-[#E5D9C0]">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-serif-display text-xl text-[#2D3640]">{b.name} <span className="text-[#5E7891] text-base">· {b.country}</span></p>
                      <p className="text-sm text-[#4A5A6B]">{b.date} · {b.time} · {b.people} pax · {b.language?.toUpperCase()} · {b.tier_name}</p>
                      <p className="text-sm text-[#5E7891] mt-1">{b.email} · {b.whatsapp}</p>
                      {b.notes && <p className="text-sm italic text-[#5E7891] mt-2">"{b.notes}"</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-3 py-1 rounded-full ${STATUS_BADGE[b.status]}`}>{b.status}</span>
                      <span className="font-serif-display text-xl text-[#2D3640]">€{b.total_price}</span>
                    </div>
                  </div>
                  {b.status === "pending" && (
                    <div className="mt-4 flex gap-2">
                      <button data-testid={`approve-${b.id}`} disabled={busy}
                        onClick={() => decide(b.id, "approved")}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D8B07A] text-[#FDFAF3] text-xs tracking-wide hover:bg-[#B89865]">
                        <Check size={14} /> Approve
                      </button>
                      <button data-testid={`reject-${b.id}`} disabled={busy}
                        onClick={() => decide(b.id, "rejected")}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#2D3640]/30 text-[#2D3640] text-xs tracking-wide hover:bg-[#EBD0D4]">
                        <X size={14} /> Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <aside className="lg:col-span-4">
            <h2 className="font-serif-display text-2xl md:text-3xl mb-5">Block dates</h2>
            <div className="p-5 rounded-2xl bg-[#FDFAF3] border border-[#E5D9C0]">
              <Calendar mode="single" selected={pickerDate} onSelect={setPickerDate} weekStartsOn={1} />
              <button data-testid="block-date-submit" onClick={blockDate}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#2D3640] text-[#FDFAF3] text-sm hover:bg-[#5E7891]">
                <Ban size={14} /> Block selected date
              </button>
            </div>

            <div className="mt-6">
              <p className="text-xs uppercase tracking-[0.22em] text-[#5E7891] mb-3 flex items-center gap-2">
                <MapPinned size={14} /> Blocked
              </p>
              <div data-testid="blocked-list" className="flex flex-wrap gap-2">
                {blocked.length === 0 && <p className="text-sm text-[#5E7891] italic">No blocked dates.</p>}
                {blocked.map((d) => (
                  <button key={d.date} data-testid={`unblock-${d.date}`} onClick={() => unblock(d.date)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F1E6D6] border border-[#E5D9C0] text-xs text-[#2D3640] hover:bg-[#EBD0D4]">
                    {d.date} <X size={12} />
                  </button>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
